import React from 'react';

import {
  assertNeverSafely,
  buildReportCellHref,
  createDocumentRoute,
  parseReportCellMentionUrl,
  REPORT_CELL_MENTION_PREFIX,
} from '@aline/common';
import { link, routes } from '@aline/routes';
import { Typography } from '@mui/material';
import { matchPath, useLocation } from 'react-router-dom';
import { AlineTooltip } from 'src/components';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { Apollo } from 'src/gql/generated';
import { handleOpenInAlineTab, openLink, useMaybeDocumentParams } from 'src/helpers';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import { AIChatCitation } from './AIChatCitation';
import {
  useAccessRequestStatusContext,
  useDocMentionAccessContext,
} from '../../../chat-hooks/useMentionAccessContext';
import {
  computeDocumentAccessData,
  DocumentMentionChip,
  ReadOnlyAlineAiMentionChip,
  ReadOnlyEmailMentionChip,
  ReadOnlyIntakeFormMentionChip,
  ReadOnlyPartyMentionChip,
  ReadOnlyPlaybookMentionChip,
  ReadOnlyPlaybookRuleMentionChip,
  ReadOnlyReportMentionChip,
  ReadOnlyTaskMentionChip,
  ReadOnlyUserMentionChip,
  ReadOnlyWorkflowMentionChip,
} from '../../../ChatPanel/ChatEditor/ChatEditorMention';
import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { createAIChatItem } from '../../helpers/aiChatItem';
import { getLinkChipDisplay } from '../../helpers/link-chip-display';
import {
  getMentionDisplayText as getMentionDisplayTextFromString,
  parseMentionUrl,
} from '../../helpers/mention-url';
import type { ParsedMentionUrl } from '../../helpers/mention-url';
import { dataIdMap, pulsingMarkdownLinkHash } from '../../remark-plugins/remark-constants';

export const useStyles = makeStyles()(theme => ({
  linkChip: {
    '&:focus': {
      backgroundColor: 'rgb(206, 206, 206)',
      textDecoration: 'none',
    },
    '&:hover': {
      backgroundColor: 'rgb(206, 206, 206)',
      textDecoration: 'none',
    },
    alignItems: 'center',
    backgroundColor: '#e0e0e0',
    borderRadius: 16,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: 9,
    maxWidth: 'min(100%, 200px)',
    overflow: 'hidden',
    padding: theme.spacing(0.25, 0.75),
    textDecoration: 'none',
    transition:
      'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    userSelect: 'none',
    verticalAlign: 'middle',
  },
  linkChipText: {
    fontSize: 9,
  },
  pulse: {
    height: 16,
    verticalAlign: 'middle',
    width: 16,
  },
}));

function getPlainText(children: React.ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(getPlainText).join('');
  if (React.isValidElement<{ children?: React.ReactNode }>(children))
    return getPlainText(children.props.children);
  return '';
}

function getMentionDisplayText(children: React.ReactNode) {
  return getMentionDisplayTextFromString(getPlainText(children));
}

export const LinkChip = React.memo(function LinkChip({ href }: { href: string }) {
  const { classes } = useStyles();
  const { label, title } = getLinkChipDisplay(href);

  return (
    <AlineTooltip arrow placement="top" title={title}>
      <a className={classes.linkChip} href={href} rel="noopener noreferrer" target="_blank">
        <Typography noWrap className={classes.linkChipText} component="span" variant="body1">
          {label}
        </Typography>
      </a>
    </AlineTooltip>
  );
});

const ReportCellCitation = React.memo(function ReportCellCitation({
  children,
  href,
}: {
  /** The report deep link from `buildReportCellHref`. */
  href: string;
  children?: React.ReactNode;
}) {
  const { classes } = useStyles();
  const displayText = getMentionDisplayText(children) || 'Report cell';

  /**
   * `applyRouteToExistingTab` is the point of this deep link: when the report
   * is already open, tab handling switches to it, and without the override
   * the tab keeps its stored route and drops the highlightCol / scrollToDoc
   * params. Modifier and non-left clicks fall through to the anchor.
   */
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) =>
      handleOpenInAlineTab(e, href, { applyRouteToExistingTab: true, title: displayText }),
    [displayText, href]
  );

  return (
    <AlineTooltip arrow placement="top" title={displayText}>
      <a
        className={classes.linkChip}
        href={href}
        rel="noreferrer"
        target="_blank"
        onClick={handleClick}
      >
        <Typography noWrap className={classes.linkChipText} component="span" variant="body1">
          {displayText}
        </Typography>
      </a>
    </AlineTooltip>
  );
});

const MarkdownDocumentMention = React.memo(function MarkdownDocumentMention({
  children,
  documentId,
  messageId,
  threadId,
}: {
  documentId: string;
  children?: React.ReactNode;
  messageId?: string;
  threadId?: string;
}) {
  const dispatch = useNotificationDispatch();
  const accessResult = useDocMentionAccessContext(documentId);
  const { documentId: currentDocumentId } = useMaybeDocumentParams();
  const location = useLocation();
  const { isPending: accessRequested } = useAccessRequestStatusContext(documentId);
  const { document, errorReason, isProcessing, loading, ocrStatus } = React.useMemo(
    () => computeDocumentAccessData(accessResult),
    [accessResult]
  );
  const displayText = getMentionDisplayText(children) || 'Untitled document';

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (errorReason === Apollo.MentionAccessErrorReason.Deleted) {
        dispatch({
          payload: {
            key: uuid4(),
            message: 'This document has been deleted and cannot be opened.',
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }

      if (errorReason === Apollo.MentionAccessErrorReason.NoAccess) {
        dispatch({
          payload: {
            key: uuid4(),
            message: "You don't have access to this document.",
            variant: 'warning',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }

      if (currentDocumentId === documentId) {
        dispatch({
          payload: {
            key: uuid4(),
            message: 'Document is already in view.',
            variant: 'info',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }

      const documentRoute =
        threadId && messageId
          ? createDocumentRoute(documentId, {
              aiChatItem: createAIChatItem({
                messageId,
                threadId,
                type: 'message',
              }),
            })
          : link.DocumentDetail({ documentId });

      /**
       * Inside the chat tab, navigate within the current tab so the user
       * doesn't lose their place; elsewhere, open a new tab. The new
       * `openLink` helper (post-#4735) understands both modes via the
       * `newTab` option — no need for the manual `useTabContext`
       * dispatch the chat panel previously had.
       */
      const inChat = Boolean(matchPath({ end: false, path: routes.ChatIndex }, location.pathname));
      openLink(documentRoute, { newTab: !inChat, title: displayText });
    },
    [
      currentDocumentId,
      dispatch,
      displayText,
      documentId,
      errorReason,
      location.pathname,
      messageId,
      threadId,
    ]
  );

  return (
    <DocumentMentionChip
      readOnly
      accessRequested={accessRequested}
      displayText={displayText}
      document={document}
      errorReason={errorReason}
      isProcessing={isProcessing}
      loading={loading}
      ocrStatus={ocrStatus}
      onOpen={handleOpen}
    />
  );
});

interface MarkdownMentionProps {
  mention: ParsedMentionUrl;
  children?: React.ReactNode;
  messageId?: string;
  threadId?: string;
}

function MarkdownMention({
  children,
  mention,
  messageId,
  threadId,
}: MarkdownMentionProps): JSX.Element | null {
  const fallbackDisplayText = getMentionDisplayText(children);

  switch (mention.type) {
    case 'aline-ai':
      return <ReadOnlyAlineAiMentionChip fallbackDisplayText={fallbackDisplayText} />;
    case 'document':
    case 'selection':
    case 'selection-v2':
    case 'selection-v3':
      return (
        <MarkdownDocumentMention documentId={mention.id} messageId={messageId} threadId={threadId}>
          {children}
        </MarkdownDocumentMention>
      );
    case 'email':
      return (
        <ReadOnlyEmailMentionChip email={mention.id} fallbackDisplayText={fallbackDisplayText} />
      );
    case 'intake-form':
      return <ReadOnlyIntakeFormMentionChip fallbackDisplayText={fallbackDisplayText} />;
    case 'party':
      return (
        <ReadOnlyPartyMentionChip fallbackDisplayText={fallbackDisplayText} partyId={mention.id} />
      );
    case 'playbook':
      return (
        <ReadOnlyPlaybookMentionChip
          fallbackDisplayText={fallbackDisplayText}
          playbookId={mention.id}
        />
      );
    case 'playbook-rule':
      return (
        <ReadOnlyPlaybookRuleMentionChip
          fallbackDisplayText={fallbackDisplayText}
          playbookRuleId={mention.id}
        />
      );
    case 'report':
      return (
        <ReadOnlyReportMentionChip
          fallbackDisplayText={fallbackDisplayText}
          reportId={mention.id}
        />
      );
    case 'task':
      return (
        <ReadOnlyTaskMentionChip fallbackDisplayText={fallbackDisplayText} taskId={mention.id} />
      );
    case 'user':
      return <ReadOnlyUserMentionChip fallbackDisplayText={fallbackDisplayText} />;
    case 'workflow':
      return (
        <ReadOnlyWorkflowMentionChip
          fallbackDisplayText={fallbackDisplayText}
          workflowId={mention.id}
        />
      );
    default:
      assertNeverSafely(mention);
      return null;
  }
}

export const AnchorElement = React.memo(function AnchorElement({
  children,
  href,
  node,
  title,
  ...restProps
}: MarkdownComponentProps<HTMLAnchorElement> & { href?: string }) {
  const { classes } = useStyles();
  const threadId = node?.properties[dataIdMap.threadId];
  const messageId = node?.properties[dataIdMap.messageId];
  const citationIndex = node?.properties[dataIdMap.citationIndex];

  /**
   * If the href is `#PULSING`, we want to show the pulsing icon.
   *
   * We are overloading an anchor, but we have to work within the
   * constraints of Markdown.
   */
  if (href === pulsingMarkdownLinkHash)
    return <PulseIcon className={classes.pulse} variant={PulseIconVariant.LoadingPrimary} />;

  const mention = href ? parseMentionUrl(href) : null;
  if (mention) {
    return (
      <MarkdownMention
        mention={mention}
        messageId={typeof messageId === 'string' ? messageId : undefined}
        threadId={typeof threadId === 'string' ? threadId : undefined}
      >
        {children}
      </MarkdownMention>
    );
  }

  if (href?.startsWith(REPORT_CELL_MENTION_PREFIX)) {
    const reportCell = parseReportCellMentionUrl(href);
    // A malformed cell link names no cell to open, so it reads as its text.
    if (!reportCell) return <>{children}</>;
    return (
      <ReportCellCitation href={buildReportCellHref(reportCell)}>{children}</ReportCellCitation>
    );
  }

  /**
   * Return regular link if not a citation.
   */
  if (
    !href ||
    typeof citationIndex !== 'number' ||
    typeof messageId !== 'string' ||
    typeof threadId !== 'string'
  ) {
    return href ? <LinkChip href={href} /> : <>{children}</>;
  }

  return (
    <AIChatCitation
      citationIndex={citationIndex}
      href={href}
      messageId={messageId}
      referencedText={typeof children === 'string' ? children : ''}
      threadId={threadId}
      {...restProps}
    />
  );
});
